// A person's pairing on their v4 results (Taylor's v4.1 doc, "The Body at Work: pairings"): the
// name, the cycle with the two gifts joined and the blind spot circled, the one line and what
// they crave; Caged and Fragmented also see the bound form, framed as the source shifting. The
// office echo shows only when their own office pattern does; a close call names the other
// pairing. "Crushed by" isn't shown: the doc doesn't say where it goes (a question for Taylor).

import { PAIR_TYPE_LINES, type Pairing } from "@giftsprofile/scoring/pairing";
import { OFFICE_TIP } from "../lib/explainers";
import { BlindSpotMark, BodyAtWorkCycle } from "./BodyAtWork";
import { Term } from "./Term";

const list = (xs: string[]) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs.at(-1)}`);
const article = (word: string) => (/^[AEIOU]/i.test(word) ? "an" : "a");

export function PairingBody({ pairing }: { pairing: Pairing }) {
  const [a, b] = pairing.gifts;
  return (
    <div data-testid="pairing">
      <p className="text-[15px] text-muted">
        {a} + {b} ·{" "}
        <Term tip={PAIR_TYPE_LINES[pairing.type]} className="text-ink">
          {pairing.type} pair
        </Term>
      </p>
      <p className="mt-3 text-[20px] font-bold leading-snug">{pairing.oneLine}</p>

      <figure className="mx-auto mt-4 max-w-[420px]">
        <BodyAtWorkCycle
          pair={[a, b]}
          blindSpot={pairing.blindSpot}
          label={`Your pairing, ${a} and ${b}, on the nine-gift cycle. Blind spot: ${list(pairing.blindSpot)}.`}
        />
        <figcaption className="mt-1 text-center text-[13px] leading-snug text-muted">
          {/* The key for the ink marker: one line however many gifts it circles. */}
          <span className="mb-2 flex items-center justify-center gap-2 text-[15px] text-ink" data-testid="blind-spot-key">
            <svg viewBox="-22 -22 44 44" className="h-6 w-6 shrink-0" aria-hidden="true">
              <BlindSpotMark />
            </svg>
            <span>
              <strong>Blind spot:</strong> {list(pairing.blindSpot)}
            </span>
          </span>
          Nine gifts, one cycle: every work of God in a church, a team or a family moves through these steps.
        </figcaption>
      </figure>

      <dl className="mt-5 flex flex-col gap-4 text-[16px] leading-snug">
        <div>
          <dt className="font-semibold">What you crave</dt>
          <dd className="mt-0.5">{pairing.craves}.</dd>
        </div>
        {pairing.showBound ? (
          <div>
            <dt className="font-semibold">When the source shifts</dt>
            <dd className="mt-0.5">
              With freedom low, the same pairing can run on the wrong fuel: {pairing.bound.charAt(0).toLowerCase() + pairing.bound.slice(1)}. The gifts
              are still real; what's driving them has changed.
            </dd>
          </div>
        ) : null}
        <div>
          <dt className="font-semibold">Your blind spot on the cycle</dt>
          <dd className="mt-0.5">
            {list(pairing.blindSpot)}. It sits farthest from your two gifts, so it points to the people you most need beside you, not to something
            wrong with you.
          </dd>
        </div>
      </dl>

      {pairing.officeEcho ? (
        <p className="mt-4 text-[16px] leading-snug">
          Your pairing echoes {article(pairing.officeEcho)} {pairing.officeEcho.toLowerCase()}'s.{" "}
          <Term tip={OFFICE_TIP} className="text-[15px] text-accent-text print:hidden">
            What does this mean?
          </Term>
        </p>
      ) : null}
      {pairing.closeCall ? (
        <p className="mt-4 rounded-[12px] bg-soft p-3 text-[15px] leading-snug" data-testid="close-call">
          Your second gift is close to {pairing.closeCall.third}; on another day your pairing might read as {pairing.closeCall.otherName}.
        </p>
      ) : null}
    </div>
  );
}
